import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Chip,
  Box,
  Tooltip
} from '@mui/material';
import {
  SwapHoriz as SwitchIcon,
  School as StudentIcon,
  SupervisorAccount as CoordinatorIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const RoleSwitcher = () => {
  const { user, availableRoles, switchRole } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);
  
  const open = Boolean(anchorEl);

  // Don't show if no other roles available
  if (!availableRoles || availableRoles.length === 0) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchRole = async (newRole) => {
    handleClose();
    setSwitching(true);

    try {
      const result = await switchRole(newRole);
      
      if (result.success) {
        toast.success(`Switched to ${newRole} mode!`);
        
        // Navigate to the appropriate dashboard
        if (newRole === 'admin') {
          navigate('/admin');
        } else if (newRole === 'student') {
          navigate('/student/dashboard');
        } else if (newRole === 'coordinator') {
          navigate('/coordinator/dashboard');
        }
        
        // Refresh the page to load correct layout
        window.location.reload();
      } else {
        toast.error(result.message || 'Failed to switch role');
      }
    } catch (error) {
      toast.error('An error occurred while switching roles');
    } finally {
      setSwitching(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'student':
        return <StudentIcon fontSize="small" />;
      case 'coordinator':
        return <CoordinatorIcon fontSize="small" />;
      case 'admin':
        return <AdminIcon fontSize="small" />;
      default:
        return <StudentIcon fontSize="small" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'student':
        return '#1976d2';
      case 'coordinator':
        return '#9c27b0';
      case 'admin':
        return '#d32f2f';
      default:
        return '#1976d2';
    }
  };

  return (
    <>
      <Tooltip title="Switch Role">
        <IconButton
          onClick={handleClick}
          disabled={switching}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            }
          }}
        >
          <SwitchIcon />
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            mt: 1.5,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Current Role
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            {getRoleIcon(user?.role)}
            <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
              {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Switch To
          </Typography>
        </Box>
        
        {availableRoles.map((role) => (
          <MenuItem
            key={role}
            onClick={() => handleSwitchRole(role)}
            disabled={switching}
            sx={{
              '&:hover': {
                backgroundColor: `${getRoleColor(role)}15`,
              }
            }}
          >
            <ListItemIcon sx={{ color: getRoleColor(role) }}>
              {getRoleIcon(role)}
            </ListItemIcon>
            <ListItemText
              primary={role.charAt(0).toUpperCase() + role.slice(1)}
              secondary={
                role === 'student' ? 'Student Portal' :
                role === 'coordinator' ? 'Coordinator Portal' :
                'Admin Portal'
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RoleSwitcher;
